import { createPortal } from "react-dom";
import { cx } from "@/lib/cx.js";
import { useOverlayBehaviour } from "@/hooks/useOverlayBehaviour.js";
import Button from "./Button.jsx";

/*
 * Centred dialog. Rendered into document.body so it is never clipped by a parent
 * with overflow — the markup's overlays sat at the end of <body> for the same
 * reason.
 *
 *   <Modal open={open} onClose={close} title="Add user" subtitle={…} icon={…}
 *          footer={<>…</>} size="md">
 *       …body…
 *   </Modal>
 *
 * Escape, backdrop click, scroll lock, initial focus, focus restore and a Tab
 * trap all come from useOverlayBehaviour.
 */

const SIZES = {
    sm: "max-w-md",
    md: "max-w-lg",
    lg: "max-w-xl",
    xl: "max-w-2xl",
    "2xl": "max-w-4xl",
    "3xl": "max-w-6xl",
};

export default function Modal({
    open,
    onClose,
    title,
    subtitle,
    icon,
    footer,
    size = "md",
    closeOnBackdrop = true,
    className,
    bodyClassName,
    children,
}) {
    const panelRef = useOverlayBehaviour({ open, onClose });

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-60">
            <div
                onClick={closeOnBackdrop ? onClose : undefined}
                className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
                aria-hidden="true"
            />

            <div className="relative flex h-full items-center justify-center p-4">
                <div
                    ref={panelRef}
                    role="dialog"
                    aria-modal="true"
                    aria-label={typeof title === "string" ? title : undefined}
                    tabIndex={-1}
                    className={cx(
                        "flex max-h-full w-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl outline-none dark:border-slate-700 dark:bg-slate-900",
                        SIZES[size] ?? SIZES.md,
                        className,
                    )}
                >
                    {(title || icon) && (
                        <OverlayHeader icon={icon} title={title} subtitle={subtitle} onClose={onClose} />
                    )}

                    <div className={cx("min-h-0 flex-1 overflow-y-auto", bodyClassName ?? "p-4.5")}>
                        {children}
                    </div>

                    {footer && (
                        <div className="flex shrink-0 items-center gap-2.5 border-t border-slate-200 px-4.5 py-3 dark:border-slate-800">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

/*
 * Right-hand slide-over. Used for the detail panels the markup opened from a
 * table row — a drawer keeps the table visible behind it, which a centred modal
 * does not.
 */
export function Drawer({
    open,
    onClose,
    title,
    subtitle,
    footer,
    size = "md",
    className,
    bodyClassName,
    children,
}) {
    const panelRef = useOverlayBehaviour({ open, onClose });

    if (!open) return null;

    const widths = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl" };

    return createPortal(
        <div className="fixed inset-0 z-60">
            <div
                onClick={onClose}
                className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
                aria-hidden="true"
            />

            <aside
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={typeof title === "string" ? title : undefined}
                tabIndex={-1}
                className={cx(
                    "absolute inset-y-0 right-0 flex w-full flex-col border-l border-slate-200 bg-white shadow-2xl outline-none dark:border-slate-700 dark:bg-slate-900",
                    widths[size] ?? widths.md,
                    className,
                )}
            >
                <OverlayHeader title={title} subtitle={subtitle} onClose={onClose} />

                <div className={cx("min-h-0 flex-1 overflow-y-auto", bodyClassName ?? "p-4.5")}>
                    {children}
                </div>

                {footer && (
                    <div className="flex shrink-0 items-center gap-2.5 border-t border-slate-200 px-4.5 py-3 dark:border-slate-800">
                        {footer}
                    </div>
                )}
            </aside>
        </div>,
        document.body,
    );
}

/** Shared header for Modal and Drawer. */
function OverlayHeader({ icon, title, subtitle, onClose, actions }) {
    return (
        <div className="flex min-h-14 shrink-0 items-center gap-3 border-b border-slate-200 px-4.5 py-3 dark:border-slate-800">
            {icon}

            <div className="min-w-0">
                {typeof title === "string" ? (
                    <p className="truncate text-sm font-semibold">{title}</p>
                ) : (
                    title
                )}
                {subtitle && (
                    <p className="truncate text-[12px] text-slate-500 dark:text-slate-400">{subtitle}</p>
                )}
            </div>

            <div className="ml-auto flex items-center gap-1.5">
                {actions}
                <Button variant="default" onClick={onClose} aria-label="Close">
                    <svg
                        className="size-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        aria-hidden="true"
                    >
                        <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                </Button>
            </div>
        </div>
    );
}

export { OverlayHeader };
